import { GraduationCap, Award, Globe, Calendar, Star, FlaskConical, BarChart3, Shield, Handshake } from "lucide-react";
import useScrollReveal from "../hooks/useScrollReveal";
import { useLanguage } from "../i18n/LanguageContext";

const credIcons = [GraduationCap, Award, Star, Globe, Calendar];
const whyIcons = [FlaskConical, Globe, BarChart3, Shield, Handshake];

export default function About() {
  const [ref, isVisible] = useScrollReveal();
  const [ref2, isVisible2] = useScrollReveal();
  const { t } = useLanguage();
  const a = t.about;

  return (
    <section id="about" data-testid="about-section" ref={ref} className="bg-[#1E3455] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Founder Bio */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="transition-all duration-700" style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? "translateX(0)" : "translateX(-28px)" }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-12 bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs uppercase tracking-widest">{a.label}</span>
            </div>
            <h2 className="text-4xl md:text-5xl text-white leading-tight mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Dr. Jaoued Ahmed</h2>
            <p className="text-[#D4AF37] text-sm uppercase tracking-widest mb-7 font-medium">{a.role}</p>
            <p className="text-slate-300 text-base leading-relaxed mb-8">{a.bio1}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {a.credentials.map((cred, i) => {
                const Icon = credIcons[i];
                return (
                  <div key={i} className="border-l-2 border-slate-700 hover:border-[#D4AF37] pl-4 transition-colors duration-300"
                    style={{ opacity: isVisible ? 1 : 0, transition: "opacity 0.7s ease-out, border-color 0.3s", transitionDelay: `${300 + i * 100}ms` }}>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon size={13} className="text-[#D4AF37] flex-shrink-0" />
                      <span className="text-white text-sm font-medium">{cred.label}</span>
                    </div>
                    <p className="text-slate-300 text-xs">{cred.detail}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="transition-all duration-700 delay-200" style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? "translateX(0)" : "translateX(28px)" }}>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border border-[#D4AF37]/15 pointer-events-none" />
              <div className="absolute -bottom-4 -right-4 w-full h-full border border-[#D4AF37]/08 pointer-events-none" />
              <img src="https://customer-assets.emergentagent.com/job_advaltis-enhance/artifacts/lgzh0xn1_image.png" alt={a.imageAlt} className="w-full h-[480px] object-cover object-top relative z-10" loading="lazy" />
              <div className="absolute bottom-6 left-6 right-6 bg-[#0B1221]/92 backdrop-blur-sm border border-slate-700/60 p-4 z-20">
                <p className="text-[#D4AF37] text-[10px] uppercase tracking-widest mb-1">{a.overlayLabel}</p>
                <p className="text-white text-sm font-medium">{a.overlayText}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Why Advaltis - Merged */}
        <div ref={ref2} className="mt-24 pt-20 border-t border-slate-800/60">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div className="transition-all duration-700" style={{ opacity: isVisible2 ? 1 : 0, transform: isVisible2 ? "translateY(0)" : "translateY(24px)" }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-12 bg-[#D4AF37]" />
                <span className="text-[#D4AF37] text-xs uppercase tracking-widest">{a.whyLabel}</span>
              </div>
              <h3 className="text-3xl md:text-4xl text-white leading-tight mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {a.whyHeading}<br /><span className="gold-gradient-text">{a.whyHeadingAccent}</span>
              </h3>
              <p className="text-slate-300 text-base leading-relaxed mb-10">{a.whyDesc}</p>
              <button data-testid="why-us-cta" onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-[#D4AF37] text-[#0B1221] px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-[#F59E0B] transition-colors duration-300">
                {a.whyCta}
              </button>
            </div>
            <div className="space-y-5">
              {a.whyItems.map((item, i) => {
                const Icon = whyIcons[i];
                return (
                  <div key={i} data-testid={`why-us-card-${i}`} className="flex gap-5 p-6 border-l-2 border-slate-800 hover:border-[#D4AF37] hover:bg-[#0B1221]/40 transition-all duration-300 group"
                    style={{ opacity: isVisible2 ? 1 : 0, transform: isVisible2 ? "translateX(0)" : "translateX(24px)", transition: "opacity 0.7s ease-out, transform 0.7s ease-out, border-color 0.3s, background-color 0.3s", transitionDelay: `${200 + i * 110}ms` }}>
                    <div className="w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4AF37]/20 transition-colors duration-300">
                      <Icon size={18} className="text-[#D4AF37]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-sm mb-2">{item.title}</h4>
                      <p className="text-slate-300 text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
